import React, { Component } from 'react';
import SignaturePad from 'signature_pad';
import './signaturefield.css';

/*
 * Wraps the signature_pad library as a form field. Emits the drawn signature
 * as a PNG data URL via onSignatureChange(fieldId, fieldName, dataUrl).
 * An empty pad emits "" so required-validation can treat it as missing.
 *
 * The canvas is sized responsively: its drawing buffer is matched to the
 * displayed size and the device pixel ratio (on mount, resize, and orientation
 * change) so pointer coordinates line up on mobile. Without this the buffer
 * (fixed pixels) and the CSS-scaled display size disagree and strokes land in
 * the wrong place / overlap on phones.
 */
export class SignatureField extends Component {
    constructor(props) {
        super(props);
        this.canvasRef = React.createRef();
    }

    componentDidMount() {
        this.signaturePad = new SignaturePad(this.canvasRef.current, {
            backgroundColor: 'rgb(255, 255, 255)',
            penColor: 'rgb(0, 0, 0)'
        });
        this.signaturePad.addEventListener('endStroke', this.handleEnd);

        this.resizeCanvas();
        window.addEventListener('resize', this.resizeCanvas);
        window.addEventListener('orientationchange', this.resizeCanvas);
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.resizeCanvas);
        window.removeEventListener('orientationchange', this.resizeCanvas);
        if (this.signaturePad) {
            this.signaturePad.removeEventListener('endStroke', this.handleEnd);
        }
    }

    /* Match the canvas buffer to its displayed size × devicePixelRatio so touch
     * coordinates map correctly. Preserves any existing strokes across resizes. */
    resizeCanvas = () => {
        const canvas = this.canvasRef.current;
        if (!canvas || !this.signaturePad) return;

        const data = this.signaturePad.toData();

        const ratio = Math.max(window.devicePixelRatio || 1, 1);
        canvas.width = canvas.offsetWidth * ratio;
        canvas.height = canvas.offsetHeight * ratio;
        canvas.getContext('2d').scale(ratio, ratio);

        this.signaturePad.clear(); // reset internal state to match the new buffer
        if (data && data.length) {
            this.signaturePad.fromData(data);
            this.emit(this.signaturePad.toDataURL('image/png'));
        }
    }

    handleEnd = () => {
        this.emit(this.signaturePad.isEmpty() ? '' : this.signaturePad.toDataURL('image/png'));
    }

    clear = (e) => {
        e.preventDefault();
        this.signaturePad.clear();
        this.emit('');
    }

    emit(dataUrl) {
        if (this.props.onSignatureChange) {
            this.props.onSignatureChange(this.props.fieldId, this.props.fieldName, dataUrl);
        }
    }

    render() {
        return (
            <div className="signature-field">
                <canvas
                    ref={this.canvasRef}
                    id={'form-field-' + this.props.fieldId}
                    className="signature-canvas"
                    role="img"
                    aria-label={(this.props.fieldName || 'Signature') + ' signature pad'}
                ></canvas>
                <div className="signature-actions">
                    <button type="button" className="signature-clear-btn" onClick={this.clear}>Clear</button>
                </div>
            </div>
        );
    }
}

export default SignatureField;
